<script setup>
// SSR-safe Teleport: при включённом SSR Teleport ломает гидрацию поддерева,
// поэтому модалку рендерим только на клиенте (после mount).
import { ref as __ref, onMounted as __onMounted } from 'vue';
const __teleportReady = __ref(false);
__onMounted(() => { __teleportReady.value = true; });

import Chart from '@/Components/Chart.vue';
import ModalPhone from '@/Components/ModalPhone.vue';
import { ref, computed, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  usluga: Object,
  lawyer: Object,
  main_usluga: Object,
  // «С чем работает»: короткие названия подуслуг направления (из short_name)
  workTopics: { type: Array, default: () => [] },
  reviewscount: Number,
  rating: Number,
  statistics: Object,
  auth: Object,
  photos: { type: Array, default: () => [] },
  borrowedPhotos: { type: Array, default: () => [] },
  borrowedUslugaPhotos: { type: Array, default: () => [] },
  phone: { type: String, default: null },
  phoneto: { type: String, default: null },
  backendurl: { type: String, default: null },
  useTrackingPhone: { type: Boolean, default: false },
  tracking: { type: Object, default: () => ({}) },
});

const slides = computed(() => {
  const result = [];
  if (props.usluga.file_path) {
    result.push({ src: 'https://nedicom.ru/' + props.usluga.file_path, id: 0 });
  }
  if (props.photos && props.photos.length > 0) {
    props.photos.forEach(p => result.push({ src: 'https://nedicom.ru/' + p.file_path, id: p.id }));
  }
  if (props.borrowedPhotos && props.borrowedPhotos.length > 0) {
    props.borrowedPhotos.forEach(p => result.push({ src: 'https://nedicom.ru/' + p.file_path, id: 'b' + p.id }));
  }
  if (props.borrowedUslugaPhotos && props.borrowedUslugaPhotos.length > 0) {
    props.borrowedUslugaPhotos.forEach(p => result.push({ src: 'https://nedicom.ru/' + p.file_path, id: 'u' + p.id }));
  }
  return result;
});

const showButton = ref(false);
const currentSlide = ref(0);
const carouselWrapper = ref(null);

function prevSlide() {
  if (currentSlide.value > 0) currentSlide.value--;
}
function nextSlide() {
  if (currentSlide.value < slides.value.length - 1) currentSlide.value++;
}
const showLawyerModal = ref(false);

// Номер виден, но последние две цифры скрыты звёздочками
const maskedPhone = computed(() => {
  const raw = props.phone ?? '';
  let count = 0;
  return raw.split('').reverse().map(ch => {
    if (/\d/.test(ch) && count < 2) { count++; return '*'; }
    return ch;
  }).reverse().join('');
});

function openLawyerModal() {
  // Цель раскрытия телефона
  // eslint-disable-next-line no-undef
  try { window.goal('OPEN_PHONE'); } catch (e) { /* noop */ }
  showLawyerModal.value = true;
}

function onCarouselWheel(e) {
  e.stopPropagation();
}

onMounted(() => {
  carouselWrapper.value?.addEventListener('wheel', onCarouselWheel, { capture: true });
});

onUnmounted(() => {
  carouselWrapper.value?.removeEventListener('wheel', onCarouselWheel, { capture: true });
});

function toggleButton() {
  showButton.value = !showButton.value;
}

function goToSlide(index) {
  currentSlide.value = index;
}

// 1 отзыв, 2 отзыва, 5 отзывов
function plural(n, one, few, many) {
  const a = Math.abs(Number(n)) % 100, b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b === 1) return one;
  if (b >= 2 && b <= 4) return few;
  return many;
}

function stars(rating) {
  return Math.min(5, Math.max(0, Math.round(rating ?? 0)));
}
</script>

<template>
  <section class="bg-white" id="about">
    <div class="px-4 mx-auto max-w-7xl lg:px-6">

      <!--
        Шапка. ⚠ ВАРИАНТ «А» ИЗ МАКЕТОВ (21.09.2026): всё о юристе — одной строкой
        под именем, чтобы кнопки связи поднялись на первый экран телефона. Раньше
        здесь был ряд плашек (просмотры, направление, стаж) плюс рейтинг отдельно
        у заголовка — три строки на то, что читается за одну.
      -->
      <div class="pt-4 pb-4">
        <h1 itemprop="name" class="text-2xl lg:text-3xl font-extrabold text-gray-900 tracking-tight leading-tight mb-3">
          {{ props.usluga.usl_name }}
        </h1>

        <div class="flex items-center gap-3">
          <button
            v-if="props.lawyer.avatar_path"
            type="button"
            @click="openLawyerModal"
            class="flex-shrink-0 group relative rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            :aria-label="'О юристе: ' + props.lawyer.name"
          >
            <img
              :src="'https://nedicom.ru/' + props.lawyer.avatar_path"
              :alt="props.lawyer.name"
              class="w-14 h-14 lg:w-12 lg:h-12 rounded-full object-cover ring-2 ring-indigo-100
                     group-hover:ring-indigo-400 transition-all duration-200"
            />
          </button>
          <div class="min-w-0">
            <div class="text-lg lg:text-xl font-extrabold text-gray-900 tracking-tight leading-tight" itemprop="brand">
              {{ props.lawyer.name }}
            </div>
            <!-- Направление и город: кто перед вами и где он принимает -->
            <div class="text-sm font-medium text-gray-700 mt-0.5">
              {{ props.main_usluga.usl_name }}<template v-if="props.usluga.cities"> · {{ props.usluga.cities.title }}</template>
            </div>
            <div class="flex items-center gap-x-1 text-sm text-gray-500 mt-0.5 whitespace-nowrap">
              <a v-if="reviewscount" href="#reviews" class="inline-flex items-center gap-1 hover:text-indigo-700">
                <svg class="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 22 20" aria-hidden="true">
                  <use href="#ic-star" />
                </svg>
                <span class="font-semibold text-gray-900">{{ props.rating }}</span>
                <span>· {{ props.reviewscount }} {{ plural(props.reviewscount, 'отзыв', 'отзыва', 'отзывов') }}</span>
              </a>
              <span v-if="props.lawyer?.expirience" class="shrink-0">
                <template v-if="reviewscount">· </template>опыт: {{ props.lawyer.expirience }} {{ plural(props.lawyer.expirience, 'год', 'года', 'лет') }}
              </span>
            </div>
          </div>
          <!-- Счётчик просмотров и график — служебное, только автору и админу -->
          <button
            v-if="props.auth && (props.auth.isadmin == 1 || props.auth.id == props.usluga.user_id)"
            @click="toggleButton" type="button"
            class="ml-auto self-start inline-flex items-center gap-1 rounded-lg px-1.5 py-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 text-xs">
            <svg class="h-3.5 w-3.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-width="2" d="M21 12c0 1.2-4.03 6-9 6s-9-4.8-9-6c0-1.2 4.03-6 9-6s9 4.8 9 6Z"/>
              <path stroke="currentColor" stroke-width="2" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
            </svg>
            <span>{{ props.usluga.counter }}</span>
          </button>
        </div>
        <meta itemprop="category" :content="props.main_usluga.usl_name" />

        <!-- «С чем работает»: темы направления, под карточкой юриста и над фото.
             Обычный текст, не кликабельно, на мобильном и десктопе. -->
        <p v-if="props.workTopics && props.workTopics.length"
          class="mt-3 text-[14px] leading-[1.45] text-gray-600">
          <b class="font-bold text-gray-900">С чем работает:</b>
          {{ props.workTopics.join(' · ') }}
        </p>
      </div>

      <!-- Фото -->
      <div class="w-full" ref="carouselWrapper">
        <div class="lg:grid lg:gap-2" style="grid-template-columns: 1fr auto">

          <!-- Основное фото -->
          <div class="relative rounded-2xl overflow-hidden lg:aspect-[5/3]">

            <!-- Мобиль: горизонтальный слайд -->
            <div
              class="flex lg:hidden transition-transform duration-300 ease-in-out"
              :style="{ transform: `translateX(-${currentSlide * 100}%)` }"
            >
              <div v-for="slide in slides" :key="slide.id" class="flex-shrink-0 w-full aspect-[2/1]">
                <img
                  itemprop="image"
                  class="w-full h-full object-cover object-[50%_30%] block"
                  :src="slide.src"
                  :alt="props.usluga.usl_name"
                  draggable="false"
                />
              </div>
            </div>

            <!-- Десктоп: абсолютное позиционирование + fade -->
            <div class="hidden lg:block h-full">
              <img
                v-for="(slide, idx) in slides"
                :key="slide.id"
                itemprop="image"
                class="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
                :class="currentSlide === idx ? 'opacity-100 z-10' : 'opacity-0 z-0'"
                :src="slide.src"
                :alt="props.usluga.usl_name"
                draggable="false"
              />
            </div>

            <!-- Стрелки -->
            <template v-if="slides.length > 1">
              <button
                v-if="currentSlide > 0"
                type="button"
                @click="prevSlide"
                class="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center z-20
                       bg-white/75 backdrop-blur-sm shadow-md text-slate-800 hover:bg-white transition-colors"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                </svg>
              </button>
              <button
                v-if="currentSlide < slides.length - 1"
                type="button"
                @click="nextSlide"
                class="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center z-20
                       bg-white/75 backdrop-blur-sm shadow-md text-slate-800 hover:bg-white transition-colors"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </button>
            </template>
          </div>

          <!-- Миниатюры справа (только десктоп) -->
          <div v-if="slides.length > 1" class="hidden lg:block w-20 relative">
            <div class="absolute inset-0 flex flex-col gap-2 overflow-y-auto pr-0.5">
              <button
                v-for="(slide, index) in slides"
                :key="slide.id"
                type="button"
                @click="goToSlide(index)"
                class="flex-shrink-0 w-full aspect-[5/3] rounded-lg overflow-hidden transition-all duration-200"
                :class="currentSlide === index ? 'ring-2 ring-indigo-500 opacity-100' : 'opacity-40 hover:opacity-70'"
              >
                <img :src="slide.src" class="w-full h-full object-cover" :alt="props.usluga.usl_name" />
              </button>
            </div>
          </div>

        </div>

        <!--
          Мобиль: точки вместо миниатюр. Пять миниатюр повторяли то же фото
          и съедали строку, из-за которой кнопки связи уходили за первый экран.
        -->
        <!-- Больше семи фото — точки превращаются в полосу шума, тогда счётчик -->
        <div v-if="slides.length > 7" class="lg:hidden mt-2 text-center text-xs text-gray-500 tabular-nums">
          {{ currentSlide + 1 }} / {{ slides.length }}
        </div>
        <div v-else-if="slides.length > 1" class="flex lg:hidden justify-center gap-1.5 mt-2">
          <button
            v-for="(slide, index) in slides"
            :key="slide.id"
            type="button"
            @click="goToSlide(index)"
            :aria-label="'Фото ' + (index + 1) + ' из ' + slides.length"
            class="h-1.5 rounded-full transition-all duration-200"
            :class="currentSlide === index ? 'w-4 bg-indigo-600' : 'w-1.5 bg-gray-300'"
          ></button>
        </div>
      </div>

      <Chart v-if="showButton" :statistics="props.statistics" />

    </div>
  </section>

  <!-- Модалка юриста -->
  <Teleport to="body" v-if="__teleportReady">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="showLawyerModal"
        class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
        @click.self="showLawyerModal = false"
      >
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 translate-y-4"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-4"
        >
          <div v-if="showLawyerModal"
            class="w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
          >
            <!-- Шапка -->
            <div class="relative bg-gradient-to-b from-indigo-50 to-white pt-8 pb-4 px-6 flex flex-col items-center gap-3">
              <button
                class="absolute top-4 right-4 text-gray-500 hover:text-gray-600 transition-colors"
                @click="showLawyerModal = false"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>

              <img
                :src="'https://nedicom.ru/' + props.lawyer.avatar_path"
                :alt="props.lawyer.name"
                class="w-24 h-24 rounded-full object-cover ring-4 ring-white shadow-lg"
              />

              <div class="text-center">
                <p class="text-lg font-bold text-gray-900">{{ props.lawyer.name }}</p>
                <p class="text-sm text-indigo-600 font-medium mt-0.5">{{ props.main_usluga.usl_name }}</p>
              </div>

              <div v-if="props.rating" class="flex items-center gap-1.5 text-sm">
                <span class="flex gap-0.5">
                  <svg v-for="i in 5" :key="i"
                    class="w-4 h-4 shrink-0"
                    :class="i <= stars(props.rating) ? 'text-yellow-300' : 'text-gray-200'"
                    fill="currentColor" viewBox="0 0 22 20">
                    <use href="#ic-star" />
                  </svg>
                </span>
                <span class="font-semibold text-gray-900">{{ props.rating }}</span>
                <span class="text-gray-500">· {{ props.reviewscount }} отзывов</span>
              </div>

              <p v-if="props.lawyer?.expirience"
                class="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-full px-3 py-1">
                <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Опыт работы: {{ props.lawyer.expirience }} лет
              </p>
            </div>

            <!-- Тело -->
            <div class="px-6 pb-6 flex flex-col gap-4">
              <p v-if="props.usluga.usl_desc" class="text-sm text-gray-600 leading-relaxed text-center">
                {{ props.usluga.usl_desc }}
              </p>

              <ModalPhone
                :phone="props.phone"
                :phoneto="props.phoneto"
                :backendurl="props.backendurl"
                :use-tracking-phone="props.useTrackingPhone"
                :tracking="props.tracking"
                goal="PHONE_CLICK"
              />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

